// WHAT A VISITOR SEES IS NOT THE ROW — it is the version the row points at.
//
// A page row is the DRAFT: the thing the editor loads, edits and saves. What is
// served is a snapshot of it, taken by `publish` and kept in
// `site-page-versions-table`, and the row carries a pointer to the one that is
// live. Saving a page changes the draft and nothing a visitor receives; pressing
// Publish copies the draft into a new version and moves the pointer; rolling
// back moves the pointer to an older version and leaves the draft exactly as it
// was. See docs/pagews.md §Publishing a page.
//
// THREE STATES, AND WHY THE THIRD EXISTS.
//
//   - PUBLISHED: `published.versionId` names a version. That version is what is
//     served, on the ROUTE the pointer carries — so the route is part of the
//     snapshot too, and editing it moves nothing until the next publish.
//   - UNPUBLISHED: the pointer is present and empty. Nothing is served; the
//     route answers 404. Every page created since publishing existed starts
//     here, which is what makes a draft a draft: an author composes a new page
//     without a visitor watching.
//   - LEGACY: the row carries no pointer at all, because it was written before
//     the field existed. Such a row is served AS IT IS — which is byte for byte
//     what it was serving the day before this shipped, and the reason the
//     state exists: a deploy that took every existing site offline until
//     somebody pressed Publish on each page is not a feature. The state is
//     transient by construction: the first write of any kind to the row
//     snapshots its content as version one BEFORE applying the write
//     (`ensurePublished` in api/code/src/model/page/publish), so a legacy page
//     that is edited keeps serving what it served, exactly as a published one
//     does. Nothing else ever creates the state.
//
// The pointer is an object with every field always present, rather than a
// nullable, for the reason every other field on a page row gives: datorm
// serializes an absent property as `null`, which is not an `object`, and
// distinguishing "never published" from "predates publishing" needs a value
// that is neither.
import { defaultOverrideValues } from "@common/schema/page-config";
import { defaultNameOf } from "@common/schema/page-element/name";

export const LEGACY = "legacy";
export const UNPUBLISHED = "unpublished";
export const PUBLISHED = "published";

// The pointer a new page is created with: present, and pointing at nothing.
export const EMPTY_PUBLISHED = () => ({
  versionId: "",
  hash: "",
  route: "",
  publishedAt: "",
});

// The partition a page's versions live in: the page's own composite key, one
// segment deeper than @common/schema/site/key's. Versions of one page are the
// only thing anybody ever asks for together.
export const pageKeyOf = (siteKey, pageId) => `${siteKey}/${pageId}`;

export const publishStateOf = (page) => {
  const pointer = page && page.published;

  if (!pointer || typeof pointer !== "object") {
    return LEGACY;
  }

  return pointer.versionId ? PUBLISHED : UNPUBLISHED;
};

// Whether a visitor gets anything at all for this page.
export const isServed = (page) => publishStateOf(page) !== UNPUBLISHED;

// The route a visitor reaches this page on: the published one, or the row's own
// for a page that predates publishing. "" — which no URL can match, see
// canonicalRoute in api/code/src/render/resolve — when nothing is served.
export const servedRouteOf = (page) => {
  switch (publishStateOf(page)) {
    case PUBLISHED:
      return page.published.route || "";
    case LEGACY:
      return (page && page.route) || "";
    default:
      return "";
  }
};

// WHAT A VERSION HOLDS, and therefore what "changed" means.
//
// Everything the composer reads off the page and a visitor can tell apart:
// the title, the route (it is the URL), the locale (it is `<html lang>` and the
// hreflang set), the elements and the shell override. NOT the editor's
// preview parameters, which compose nothing on a served page, and NOT
// `translationPending`, which is a note to the translator about an element
// rather than a property of the element — clearing it must not make a page
// read as changed when nothing a visitor sees has.
//
// Every optional field is given the default the model gives it on write, so a
// row that predates a field hashes the same before and after the write that
// fills it in. Without that, the first save of a legacy page would read as a
// change to publish when the visitor's document was identical.
//
// `name` runs the other way, for the same reason. An unnamed element is named
// on write from its type and id (@common/schema/page-element/name, P266), but a
// published page's hash was taken while it held "" — so the DEFAULT name hashes
// as "", and naming it neither makes a published page read as changed nor
// changes the hash of any row written before the default existed.
export const publishedContentOf = (page) => ({
  title: (page && page.title) || "",
  route: (page && page.route) || "",
  locale: (page && page.locale) || "",
  elements: ((page && page.elements) || []).map((element, index) => ({
    elementid: element.elementid,
    name: element.name && element.name !== defaultNameOf(element, index) ? element.name : "",
    type: element.type,
    tags: element.tags || [],
    frame: element.frame || "<div><%-content%></div>",
    config: element.config || {},
  })),
  config: (page && page.config) || defaultOverrideValues(),
});

// JSON with the keys of every object in sorted order, so that two objects that
// are equal hash equal whatever order a form, a file or DynamoDB handed their
// keys back in. Arrays keep their order — for elements it IS the content.
export const stableStringify = (value) => {
  if (Array.isArray(value)) {
    return `[${value.map(stableStringify).join(",")}]`;
  }

  if (value && typeof value === "object") {
    return `{${Object.keys(value)
      .sort()
      .map((key) => `${JSON.stringify(key)}:${stableStringify(value[key])}`)
      .join(",")}}`;
  }

  return JSON.stringify(value === undefined ? null : value);
};

export default [
  {
    id: "/Page/Published",
    type: "object",
    properties: {
      versionId: { type: "string" },
      // sha256 of stableStringify(publishedContentOf(page)) at the moment the
      // version was taken. Kept on the pointer so "has the draft changed" is a
      // comparison and not a read of the version row.
      hash: { type: "string" },
      // The route that version is served on — the one field of the snapshot
      // the render path needs before it has read the version.
      route: { type: "string" },
      // ISO datetime. When this content went live, which after a rollback is
      // the rollback and not the version's own date.
      publishedAt: { type: "string" },
    },
    required: ["versionId", "hash", "route", "publishedAt"],
  },
];
